import { useCallback, useEffect, useRef } from 'react';
import {
  getPostHogSessionId,
  useTrackEvent,
} from 'shared/utils/useTrackEvent';

type NavSection = { options: { text: string }[] };

type ActiveSection = {
  section: string;
  sectionIndex: number;
  enteredAt: number;
  visitNumber: number;
  previousSection: string | null;
};

interface SectionTrackingOptions {
  currentSection: string | undefined;
  sections: NavSection[];
  reportEventData: Record<string, unknown>;
  enabled: boolean;
}

export const useSectionTracking = ({
  currentSection,
  sections,
  reportEventData,
  enabled,
}: SectionTrackingOptions) => {
  const trackEvent = useTrackEvent();

  const activeSectionRef = useRef<ActiveSection | null>(null);
  const sectionVisitCountsRef = useRef<Record<string, number>>({});
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const ctxRef = useRef({ reportEventData });
  ctxRef.current = { reportEventData };

  const getSectionIndex = useCallback((sectionText: string) => {
    let idx = 0;
    for (const sec of sectionsRef.current) {
      for (const opt of sec.options) {
        if (opt.text === sectionText) return idx;
        idx += 1;
      }
    }
    return -1;
  }, []);

  useEffect(() => {
    if (!enabled || !currentSection) return;
    const prev = activeSectionRef.current;
    if (prev && prev.section === currentSection) return;
    const now = Date.now();
    const sessionId = getPostHogSessionId();
    if (prev) {
      trackEvent('ReportSectionExited', {
        ...reportEventData,
        sessionId,
        section: prev.section,
        sectionIndex: getSectionIndex(prev.section),
        visitNumber: prev.visitNumber,
        previousSection: prev.previousSection,
        durationMs: now - prev.enteredAt,
      });
    }
    const visitNumber =
      (sectionVisitCountsRef.current[currentSection] ?? 0) + 1;
    sectionVisitCountsRef.current[currentSection] = visitNumber;
    trackEvent('ReportSectionEntered', {
      ...reportEventData,
      sessionId,
      section: currentSection,
      sectionIndex: getSectionIndex(currentSection),
      visitNumber,
      previousSection: prev?.section ?? null,
    });
    activeSectionRef.current = {
      section: currentSection,
      sectionIndex: getSectionIndex(currentSection),
      enteredAt: now,
      visitNumber,
      previousSection: prev?.section ?? null,
    };
  }, [enabled, currentSection, reportEventData, getSectionIndex, trackEvent]);

  useEffect(() => {
    const flushExit = () => {
      const prev = activeSectionRef.current;
      if (!prev) return;
      trackEvent('ReportSectionExited', {
        ...ctxRef.current.reportEventData,
        sessionId: getPostHogSessionId(),
        section: prev.section,
        sectionIndex: prev.sectionIndex,
        visitNumber: prev.visitNumber,
        previousSection: prev.previousSection,
        durationMs: Date.now() - prev.enteredAt,
      });
      activeSectionRef.current = null;
    };
    window.addEventListener('beforeunload', flushExit);
    return () => {
      flushExit();
      window.removeEventListener('beforeunload', flushExit);
    };
  }, [trackEvent]);
};
